/**
 * Today's assistant usage as the backend sends it — `usageUsed` and `usageLimit` in tokens, on every
 * answer and from `assistantUsage` when the window opens — turned into what the window shows: a share
 * of the day's limit, never the token counts, which mean nothing to the person reading.
 *
 * The limit counts tokens, and a question costs a varying number of them, so a share is the one
 * figure that stays true: "16 questions left" would be a guess.
 */

/** `{ used, limit }` from a response's data, or null when it carries no usage. */
export const usageFrom = (data) => {
    if (!data || data.usageUsed === undefined || data.usageLimit === undefined) return null;
    const used = Number(data.usageUsed);
    const limit = Number(data.usageLimit);
    if (!Number.isFinite(used) || !Number.isFinite(limit)) return null;
    return { used: Math.max(0, used), limit: Math.max(0, limit) };
};

/** Whole percent of the limit used, 0–100; null when there is no limit to measure against. */
export const usageShare = (usage) => {
    if (!usage || usage.limit <= 0) return null;
    return Math.min(100, Math.floor((usage.used / usage.limit) * 100));
};

/** "full" once the limit is reached, "warning" from 80%, otherwise "normal". */
export const usageTone = (share) => {
    if (share === null || share === undefined) return "normal";
    if (share >= 100) return "full";
    if (share >= 80) return "warning";
    return "normal";
};

/** The line under the composer. */
export const usageLabel = (share) =>
    share >= 100 ? "Daily limit reached — it resets at 00:00 UTC" : `Today: ${share}% of your daily limit`;
